import { useReactFlow, NodeProps } from '@xyflow/react'
import DOMPurify from 'dompurify'
import { ExternalLink } from 'lucide-react'
import MarkdownIt from 'markdown-it'
import mermaid from 'mermaid'
import React, { useState, useEffect, useRef, useMemo, memo } from 'react'
import NodeDataSettingsDialog, { DataField } from '@/components/dialogs/data-settings-dialog/NodeDataSettingsDialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'

// Import AI command utilities
import useDataSettings from '@/hooks/useDataSettings'
import {
  AICommand,
  AICommandType,
  AICommandStatus,
  createAICommand,
  isCommandComplete,
  isCommandError,
  createNodeCommandPayload,
  useAICommandState,
} from '@/utils/aiCommandUtils'
import NodeBaseActions from '../base/base-actions/NodeBaseActions'
import NodeBaseContainer from '../base/base-container/NodeBaseContainer'

// Configure markdown-it
const mdIt = new MarkdownIt()

interface NodeDiagramsMermaidData {
  content?: string
  renderedDiagram?: string
  statusMessage?: string
  label?: string
  isMinimized?: boolean
  isLocked?: boolean
  aiSuggestions?: string[]
  designPrompt?: string
  diagramType?: string
  showDesignMode?: boolean
  assignedEmails?: string[] | string
  documentRequest?: AICommand
  minimizeLevel?: 'normal' | 'medium' | 'minimum'
  minifiedInfo?: {
    type?: string
    content?: string
    prompt?: string
  }
  dataSettings?: any
}

const DEFAULT_DIAGRAM = `graph TD
    A[Start] --> B{Is it?}
    B -->|Yes| C[OK]
    B -->|No| D[End]`

const DIAGRAM_TYPES = [
  { value: 'flowchart', label: 'Flowchart' },
  { value: 'sequence', label: 'Sequence Diagram' },
  { value: 'class', label: 'Class Diagram' },
  { value: 'state', label: 'State Diagram' },
  { value: 'er', label: 'Entity Relationship Diagram' },
  { value: 'gantt', label: 'Gantt Chart' },
  { value: 'pie', label: 'Pie Chart' },
  { value: 'mindmap', label: 'Mind Map' },
  { value: 'timeline', label: 'Timeline' },
  { value: 'journey', label: 'User Journey' },
]

const DIAGRAM_HELP = {
  flowchart: 'Shows process flow with nodes, decisions, and connections',
  sequence: 'Shows interactions between actors over time',
  class: 'Shows classes, interfaces, and their relationships',
  state: 'Shows states and transitions between them',
  er: 'Shows entities, attributes and relationships for data modeling',
  gantt: 'Shows project schedule with tasks and milestones',
  pie: 'Shows data as slices of a pie chart',
  mindmap: 'Shows hierarchical information and connections between ideas',
  timeline: 'Shows events along a timeline',
  journey: 'Shows user experience journey with emotions and touchpoints',
}

const EXAMPLE_PROMPTS = {
  flowchart: 'Create a flowchart showing the process of user registration with email verification',
  sequence: 'Create a sequence diagram showing user login process with authentication server',
  class: 'Create a class diagram for an e-commerce system with User, Product, and Order classes',
  state: 'Create a state diagram for an order processing system',
  er: 'Create an entity relationship diagram for a library management system',
  gantt: 'Create a Gantt chart for a web development project with phases',
  pie: 'Create a pie chart showing the distribution of programming languages in a project',
  mindmap: 'Create a mind map for project planning and management',
  timeline: 'Create a timeline showing the evolution of web technologies',
  journey: 'Create a user journey map for an online shopping experience',
}

const NodeDiagramsMermaid: React.FC<NodeProps & { data: NodeDiagramsMermaidData }> = (
  props
) => {
  const { id, data, selected } = props
  const { updateNodeData } = useReactFlow()

  // Collaborative state: local copies, initialized empty, synced from data.<field>
  const [localContent, setLocalContent] = useState('')
  const [localRenderedDiagram, setLocalRenderedDiagram] = useState('')
  const [localStatusMessage, setLocalStatusMessage] = useState('')
  const [localAiSuggestions, setLocalAiSuggestions] = useState<string[]>([])
  const [localIsLocked, setLocalIsLocked] = useState(false)
  const [localIsMinimized, setLocalIsMinimized] = useState(false)
  const [localEditingTitleValue, setLocalEditingTitleValue] = useState('')
  const [localShowDesignMode, setLocalShowDesignMode] = useState(false)
  const [localDesignPrompt, setLocalDesignPrompt] = useState('')
  const [localDiagramType, setLocalDiagramType] = useState('flowchart')
  const [localAssignedEmails, setLocalAssignedEmails] = useState<string[]>([])
  const [minimizeLevel, setMinimizeLevel] = useState<
    'normal' | 'medium' | 'minimum'
  >('normal')
  const [isMinimized, setIsMinimized] = useState(false)

  // Local UI-only state
  const [localIsEditingTitle, setLocalIsEditingTitle] = useState(false)
  const [localDiagramError, setLocalDiagramError] = useState<string | null>(null)
  const [localShowHelpDialog, setLocalShowHelpDialog] = useState(false)
  const [localRenderedSuggestions, setLocalRenderedSuggestions] = useState<string[]>([])
  const [localShowDeleteDialog, setLocalShowDeleteDialog] = useState(false)
  const [localItemToDelete, setLocalItemToDelete] = useState(null)
  const [localIsGenerating, setLocalIsGenerating] = useState(false)
  const [isDataSettingsDialogVisible, setIsDataSettingsDialogVisible] = useState(false)

  const titleInputRef = useRef<HTMLInputElement>(null)
  const diagramContainerRef = useRef<HTMLDivElement>(null)
  const promptTextareaRef = useRef<HTMLTextAreaElement>(null)
  const diagramTextareaRef = useRef<HTMLTextAreaElement>(null)

  // Initialize Mermaid
  useEffect(() => {
    try {
      mermaid.initialize({
        startOnLoad: true,
        theme: 'default',
        securityLevel: 'loose',
        fontFamily: 'arial',
        flowchart: {
          useMaxWidth: true,
          htmlLabels: true
        },
        // Add error handling configuration
        logLevel: 'error'
      });
      console.log('Mermaid initialized successfully');
    } catch (error) {
      console.error('Mermaid initialization error:', error);
    }
  }, []);

  // Sync local collaborative state from data.<field>
  useEffect(() => {
    setLocalContent(data.content ?? DEFAULT_DIAGRAM)
  }, [data.content])
  useEffect(() => {
    setLocalRenderedDiagram(data.renderedDiagram ?? '')
  }, [data.renderedDiagram])
  useEffect(() => {
    setLocalStatusMessage(data.statusMessage ?? '')
  }, [data.statusMessage])
  useEffect(() => {
    setLocalAiSuggestions(data.aiSuggestions ?? [])
  }, [data.aiSuggestions])
  useEffect(() => {
    setLocalIsLocked(data.isLocked ?? false)
  }, [data.isLocked])
  useEffect(() => {
    setLocalIsMinimized(data.isMinimized ?? false)
  }, [data.isMinimized])
  useEffect(() => {
    setLocalEditingTitleValue(data.label ?? '')
  }, [data.label])
  useEffect(() => {
    setLocalShowDesignMode(data.showDesignMode ?? false)
  }, [data.showDesignMode])
  useEffect(() => {
    setLocalDesignPrompt(data.designPrompt ?? '')
  }, [data.designPrompt])
  useEffect(() => {
    setLocalDiagramType(data.diagramType ?? 'flowchart')
  }, [data.diagramType])
  useEffect(() => {
    setLocalAssignedEmails(
      Array.isArray(data.assignedEmails)
        ? data.assignedEmails
        : data.assignedEmails
          ? data.assignedEmails.trim().split(/\s+/).filter(Boolean)
          : []
    )
  }, [data.assignedEmails])

  // Initialize minimize level from data
  useEffect(() => {
    if (data.minimizeLevel === undefined) {
      updateNodeData(id, { ...data, minimizeLevel: 'normal' })
    } else {
      setMinimizeLevel(data.minimizeLevel)
      setIsMinimized(data.minimizeLevel !== 'normal')
    }
  }, [data.minimizeLevel, id, data, updateNodeData])

  // Keep minifiedInfo in sync with source data
  useEffect(() => {
    const minifiedInfo: Record<string, any> = {}

    if (data.diagramType) {
      minifiedInfo.type = data.diagramType
    }

    if (data.content) {
      minifiedInfo.content = 'Generated'
    }

    if (data.designPrompt && typeof data.designPrompt === 'string' && data.designPrompt.trim()) {
      minifiedInfo.prompt =
        data.designPrompt.substring(0, 50) + (data.designPrompt.length > 50 ? '...' : '')
    }

    // Only update if there are actual changes
    const currentMinified = data.minifiedInfo || {}
    const hasChanges =
      JSON.stringify(currentMinified) !== JSON.stringify(minifiedInfo)

    if (hasChanges) {
      updateNodeData(id, { ...data, minifiedInfo })
    }
  }, [
    data.diagramType,
    data.content,
    data.designPrompt,
    data.minifiedInfo,
    id,
    data,
    updateNodeData,
  ])

  // Helper to sync node data (collaborative state)
  const syncNodeData = (newData: Partial<NodeDiagramsMermaidData>) => {
    updateNodeData(id, { ...data, ...newData })
  }

  // Function to render Mermaid diagram (collaborative update only)
  const renderDiagram = async () => {
    try {
      setLocalDiagramError(null)
      
      console.log('Rendering Mermaid diagram with content:', localContent)
      
      // Use mermaid.render to generate SVG
      const diagramId = `mermaid-${id}-${Date.now()}`
      const { svg } = await mermaid.render(diagramId, localContent)
      
      console.log('Mermaid rendering successful, SVG length:', svg.length)
      
      syncNodeData({
        renderedDiagram: svg,
        content: localContent,
        statusMessage: 'Diagram rendered successfully',
      })
    } catch (error) {
      console.error('Mermaid rendering error:', error)
      const errorMessage = error instanceof Error ? error.message : String(error)
      console.error('Content that failed to render:', localContent)
      
      setLocalDiagramError(
        `Error rendering diagram: ${errorMessage}`
      )
      syncNodeData({ statusMessage: `Error rendering diagram: ${errorMessage}` })
    }
  }

  // Render diagram on initial load if needed
  useEffect(() => {
    const initialRender = async () => {
      if (!data.renderedDiagram && data.content) {
        await renderDiagram()
      }
    }
    initialRender()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Check content for errors before rendering
  const validateDiagram = () => {
    if (!localContent.trim()) {
      setLocalDiagramError('Diagram content cannot be empty')
      return false
    }

    // Basic Mermaid syntax validation
    const mermaidKeywords = ['graph', 'flowchart', 'sequenceDiagram', 'classDiagram', 'stateDiagram', 'erDiagram', 'gantt', 'pie', 'mindmap', 'timeline', 'journey']
    const hasValidKeyword = mermaidKeywords.some(keyword => localContent.includes(keyword))
    
    if (!hasValidKeyword) {
      setLocalDiagramError('Diagram must contain valid Mermaid syntax')
      return false
    }

    setLocalDiagramError(null)
    return true
  }

  // Handle lock/unlock
  const handleLock = () => {
    syncNodeData({ isLocked: !localIsLocked })
  }

  // Handle minimize/maximize
  const handleMinimize = () => {
    syncNodeData({ isMinimized: !localIsMinimized })
  }

  // Handle delete
  const confirmDelete = (item) => {
    setLocalItemToDelete(item)
    setLocalShowDeleteDialog(true)
  }

  const handleDeleteConfirmed = () => {
    if (localItemToDelete) {
      if ((window as any).__flowDeleteNode) {
        ;(window as any).__flowDeleteNode(id)
      }
    }
    setLocalShowDeleteDialog(false)
    setLocalItemToDelete(null)
  }

  // Handle duplicate
  const handleDuplicate = () => {
    if ((window as any).__flowDuplicateNode) {
      ;(window as any).__flowDuplicateNode(id)
    }
  }

  // Function to handle title edit save
  const handleTitleSave = () => {
    syncNodeData({ label: localEditingTitleValue })
    setLocalIsEditingTitle(false)
  }

  // Function to handle keypresses in title input
  const handleTitleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleTitleSave()
    } else if (e.key === 'Escape') {
      setLocalIsEditingTitle(false)
      setLocalEditingTitleValue(data.label || '')
    }
  }

  // Focus input when editing starts
  useEffect(() => {
    if (localIsEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus()
      titleInputRef.current.select()
    }
  }, [localIsEditingTitle])

  // Toggle between design mode and code mode
  const toggleDesignMode = () => {
    syncNodeData({ showDesignMode: !localShowDesignMode })
  }

  // Use AI command state for diagram generation
  const diagramGenerateState = useAICommandState(
    data.documentRequest,
    AICommandType.DIAGRAMS_MERMAID_GENERATE
  )
  const diagramDesignState = useAICommandState(
    data.documentRequest,
    AICommandType.DIAGRAMS_MERMAID_DESIGN
  )

  // Update isGenerating based on command states
  useEffect(() => {
    setLocalIsGenerating(
      diagramGenerateState.isProcessing || diagramDesignState.isProcessing
    )
  }, [diagramGenerateState.isProcessing, diagramDesignState.isProcessing])

  // Handle generating AI suggestions for diagram improvement
  const handleGetAISuggestions = () => {
    if (!localContent.trim()) {
      syncNodeData({
        statusMessage: 'Error: Please enter a diagram to analyze.',
      })
      return
    }
    const processingMsg = 'Generating AI suggestions...'
    syncNodeData({ statusMessage: processingMsg })
    const documentRequest = createAICommand(
      AICommandType.DIAGRAMS_MERMAID_GENERATE,
      AICommandStatus.STARTED,
      createNodeCommandPayload(id, {
        content: localContent,
        document: 'mermaid-diagram-suggestions',
      })
    )
    syncNodeData({ documentRequest })
  }

  // Generate diagram from natural language prompt
  const handleGenerateDiagramFromPrompt = () => {
    if (!localDesignPrompt.trim()) {
      syncNodeData({
        statusMessage: 'Error: Please enter a design description.',
      })
      return
    }
    const processingMsg = 'Generating diagram from description...'
    syncNodeData({
      statusMessage: processingMsg,
      documentRequest: createAICommand(
        AICommandType.DIAGRAMS_MERMAID_DESIGN,
        AICommandStatus.STARTED,
        createNodeCommandPayload(id, {
          prompt: localDesignPrompt,
          diagramType: localDiagramType,
          document: 'mermaid-diagram-generation',
        })
      ),
    })
  }

  // Use example prompt for selected diagram type
  const useExamplePrompt = () => {
    const example = EXAMPLE_PROMPTS[localDiagramType] || ''
    setLocalDesignPrompt(example)
    if (promptTextareaRef.current) {
      promptTextareaRef.current.focus()
    }
  }

  // Function to clear AI suggestions
  const clearAiSuggestions = () => {
    syncNodeData({ aiSuggestions: [], statusMessage: 'AI suggestions cleared' })
    setLocalRenderedSuggestions([])
  }

  // Add effect to update AI suggestions when system agent has finished processing
  useEffect(() => {
    if (!data.documentRequest) return

    // Handle diagram suggestions generation completion
    if (
      isCommandComplete(data.documentRequest, AICommandType.DIAGRAMS_MERMAID_GENERATE)
    ) {
      if (data.documentRequest.suggestions) {
        const suggestions = data.documentRequest.suggestions
        syncNodeData({
          aiSuggestions: suggestions,
          statusMessage: 'AI suggestions generated.',
          documentRequest: undefined,
        })
        setLocalRenderedSuggestions([])
        suggestions.forEach(async (suggestion, index) => {
          try {
            const suggestionId = `suggestion-${id}-${index}-${Date.now()}`
            const { svg } = await mermaid.render(suggestionId, suggestion)
            setLocalRenderedSuggestions((prev) => {
              const newRendered = [...prev]
              newRendered[index] = svg
              return newRendered
            })
          } catch (error) {
            // ignore rendering error for suggestion preview
          }
        })
      } else {
        syncNodeData({
          statusMessage: 'AI suggestions generated.',
          documentRequest: undefined,
        })
      }
    } else if (
      isCommandError(data.documentRequest, AICommandType.DIAGRAMS_MERMAID_GENERATE)
    ) {
      syncNodeData({
        statusMessage: 'Error generating AI suggestions.',
        documentRequest: undefined,
      })
    }

    // Handle design generation completion
    if (
      isCommandComplete(data.documentRequest, AICommandType.DIAGRAMS_MERMAID_DESIGN)
    ) {
      if (data.documentRequest.generatedDiagram) {
        const generatedContent = data.documentRequest.generatedDiagram
        const renderGeneratedDiagram = async () => {
          try {
            const generatedId = `generated-${id}-${Date.now()}`
            console.log('Rendering Mermaid diagram with content:', generatedContent)
            const { svg } = await mermaid.render(generatedId, generatedContent)
            console.log('Mermaid rendering successful, SVG length:', svg.length)
            syncNodeData({
              content: generatedContent,
              renderedDiagram: svg,
              statusMessage: 'Diagram generated successfully!',
              showDesignMode: false,
              documentRequest: undefined,
            })
            setTimeout(() => {
              if (diagramTextareaRef.current) {
                diagramTextareaRef.current.focus()
              }
            }, 100)
          } catch (error) {
            console.error('Mermaid rendering error:', error)
            const errorMessage = error instanceof Error ? error.message : String(error)
            console.error('Generated content that failed:', generatedContent)
            syncNodeData({
              statusMessage: `Error rendering generated diagram: ${errorMessage}`,
              documentRequest: undefined,
            })
          }
        }
        renderGeneratedDiagram()
      } else {
        syncNodeData({
          statusMessage:
            'Error generating diagram. Please try a different description.',
          documentRequest: undefined,
        })
      }
    } else if (
      isCommandError(data.documentRequest, AICommandType.DIAGRAMS_MERMAID_DESIGN)
    ) {
      syncNodeData({
        statusMessage:
          'Error generating diagram. Please try a different description.',
        documentRequest: undefined,
      })
    }
  }, [data.documentRequest])

  // Handle refresh button click
  const handleRefreshDiagram = async () => {
    if (validateDiagram()) {
      await renderDiagram()
    } else {
      syncNodeData({ statusMessage: `Error: ${localDiagramError}` })
    }
  }

  // Apply an AI suggestion to the current diagram
  const applySuggestion = (suggestion: string) => {
    syncNodeData({ content: suggestion })
    // No setLocalContent here; will update via useEffect on data.content
  }

  // Handle diagram type change
  const handleDiagramTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    syncNodeData({ diagramType: e.target.value })
  }

  // Add a handler for title change to sync with node data
  const handleTitleChange = (newTitle: string) => {
    syncNodeData({ label: newTitle })
  }

  const handleMinimizeLevelChange = (
    level: 'normal' | 'medium' | 'minimum'
  ) => {
    setMinimizeLevel(level)
    setIsMinimized(level !== 'normal')
    updateNodeData(id, {
      ...data,
      minimizeLevel: level,
      isMinimized: level !== 'normal',
    })
  }

  // --- Data Settings logic (outgoing only) ---
  // Only expose outgoing data (no inputLinks)
  const outgoingFields = {
    content: {
      value: localContent,
      enabled: true,
      label: 'Diagram Code',
      description: 'Mermaid diagram code',
      permissions: { readable: true, writable: false },
    },
    renderedDiagram: {
      value: localRenderedDiagram,
      enabled: !!localRenderedDiagram,
      label: 'Rendered Diagram',
      description: 'Rendered diagram SVG',
      permissions: { readable: true, writable: false },
    },
    diagramType: {
      value: localDiagramType,
      enabled: true,
      label: 'Diagram Type',
      description: 'Type of diagram (flowchart, sequence, etc.)',
      permissions: { readable: true, writable: false },
    },
    designPrompt: {
      value: localDesignPrompt,
      enabled: !!localDesignPrompt,
      label: 'Design Prompt',
      description: 'Natural language prompt for diagram generation',
      permissions: { readable: true, writable: false },
    },
    aiSuggestions: {
      value: JSON.stringify(localAiSuggestions),
      enabled: localAiSuggestions.length > 0,
      label: 'AI Suggestions',
      description: 'AI-generated diagram suggestions',
      permissions: { readable: true, writable: false },
    },
  }

  // Use useDataSettings for outgoing/exposed data only
  const {
    dataSettings,
    setDataSettings,
    handleDataSettingsChange,
    handleOpenDataSettingsDialog,
  } = useDataSettings(
    id,
    data,
    (newData) => syncNodeData(newData),
    false, // showDebugInfo
    '', // context not relevant here
    new Map(), // sections not relevant
    '', // contentMarkdown not relevant
    setIsDataSettingsDialogVisible
  )

  const handleRemoveInputLink = (linkId: string) => {
    // Handle removing input links
    console.log('Remove input link:', linkId)
  }

  const handleSyncInputLink = async (linkId: string): Promise<{ success: boolean; newData?: Record<string, DataField> }> => {
    // Handle syncing input links
    console.log('Sync input link:', linkId)
    return { success: true }
  }

  // Always keep outgoing/exposedData up to date
  useEffect(() => {
    // Only outgoing/exposedData, no inputLinks
    const newDataSettings = {
      ...dataSettings,
      inputLinks: [],
      exposedData: outgoingFields,
      availableNodes: [],
      outputLinks: dataSettings?.outputLinks || [],
      autoSync: false,
    }
    if (
      JSON.stringify(dataSettings?.exposedData) !==
      JSON.stringify(outgoingFields)
    ) {
      setDataSettings(newDataSettings)
      syncNodeData({ dataSettings: newDataSettings })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    data.content,
    data.renderedDiagram,
    data.diagramType,
    data.designPrompt,
    data.aiSuggestions,
  ])

  // Compute status for NodeBaseContainer based on statusMessage
  const nodeStatus = useMemo(() => {
    if (!data?.statusMessage) return 'initial'
    const lowerMessage = data.statusMessage.toLowerCase()
    if (lowerMessage.includes('loading')) return 'loading'
    if (lowerMessage.includes('error')) return 'error'
    if (
      lowerMessage.includes('complete') ||
      lowerMessage.includes('successful')
    )
      return 'success'
    return 'initial'
  }, [data?.statusMessage])

  return (
    <NodeBaseContainer
      id={id}
      data={data}
      resizable={!localIsLocked}
      selected={selected}
      isProcessing={localIsGenerating}
      assignedEmails={localAssignedEmails}
      onAssignUsersClick={() => {
        const emailsStr = window.prompt(
          'Enter emails separated by space',
          localAssignedEmails.join(' ')
        )
        if (emailsStr !== null) {
          const emailsArr = emailsStr.trim().split(/\s+/).filter(Boolean)
          syncNodeData({ assignedEmails: emailsArr })
        }
      }}
      nodeTitle={data.label || 'Mermaid Diagram'}
      onTitleChange={handleTitleChange}
      status={nodeStatus}
      minimizeLevel={data.minimizeLevel || 'normal'}
      onMinimizeLevelChange={handleMinimizeLevelChange}
    >
      {(data.minimizeLevel || 'normal') === 'normal' && (
        <div className="flex flex-col h-full">
          <div className="flex justify-between items-center px-2 py-3">
            <NodeBaseActions
              nodeId={id}
              onDelete={() => confirmDelete(null)}
              onDuplicate={handleDuplicate}
              onLock={handleLock}
              onUnlock={localIsLocked}
              onMinimize={handleMinimize}
              onMaximize={localIsMinimized}
            />
            <div className="flex items-center space-x-2">
              <button
                className="px-2 py-1 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors"
                onClick={() => setLocalShowHelpDialog(true)}
              >
                Help
              </button>
            </div>
          </div>

          {!localIsMinimized && (
            <div className="flex flex-col flex-grow px-2 pb-2 space-y-2">
              {!localIsLocked && (
                <div className="flex space-x-2 border-b pb-2">
                  <button
                    className={`px-4 py-1 rounded-t transition-colors ${
                      !localShowDesignMode
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                    }`}
                    onClick={() => localShowDesignMode && toggleDesignMode()}
                  >
                    Code Mode
                  </button>
                  <button
                    className={`px-4 py-1 rounded-t transition-colors ${
                      localShowDesignMode
                        ? 'bg-green-600 text-white'
                        : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                    }`}
                    onClick={() => !localShowDesignMode && toggleDesignMode()}
                  >
                    Design Mode
                  </button>
                </div>
              )}

              {localShowDesignMode ? (
                // Design Mode UI
                <div className="flex-grow flex flex-col">
                  <div className="mb-2">
                    <label className="block text-sm font-medium mb-1">
                      Diagram Type:
                    </label>
                    <div className="flex mb-2">
                      <select
                        className="flex-1 p-2 border rounded"
                        value={localDiagramType}
                        onChange={handleDiagramTypeChange}
                        disabled={localIsLocked || localIsGenerating}
                      >
                        {DIAGRAM_TYPES.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                      <button
                        className="ml-2 px-3 py-1 bg-gray-200 text-gray-800 text-sm rounded hover:bg-gray-300 transition-colors"
                        onClick={useExamplePrompt}
                        disabled={localIsLocked || localIsGenerating}
                        title="Use an example prompt for this diagram type"
                      >
                        Example
                      </button>
                    </div>
                    <p className="text-xs text-gray-600 mb-2">
                      {DIAGRAM_HELP[localDiagramType] ||
                        'Select a diagram type to see description'}
                    </p>
                  </div>
                  <div className="flex-grow flex flex-col">
                    <label className="block text-sm font-medium mb-1">
                      Describe what you want to diagram (be specific):
                    </label>
                    <textarea
                      ref={promptTextareaRef}
                      className="flex-grow p-2 border rounded nodrag resize-none font-sans text-sm mb-2"
                      placeholder={`Example: ${EXAMPLE_PROMPTS[localDiagramType] || 'Describe your diagram here...'}`}
                      value={localDesignPrompt}
                      onChange={(e) => {
                        setLocalDesignPrompt(e.target.value)
                      }}
                      onBlur={(e) =>
                        syncNodeData({ designPrompt: e.target.value })
                      }
                      disabled={localIsLocked || localIsGenerating}
                    />
                  </div>
                  <button
                    className="w-full py-2 px-4 bg-green-600 text-white rounded hover:bg-green-700 transition-colors disabled:bg-gray-400"
                    onClick={handleGenerateDiagramFromPrompt}
                    disabled={
                      localIsLocked ||
                      localIsGenerating ||
                      !localDesignPrompt.trim()
                    }
                  >
                    {localIsGenerating
                      ? 'Generating Diagram...'
                      : 'Generate Diagram'}
                  </button>
                </div>
              ) : (
                // Code Mode UI
                <div className="flex-grow flex flex-col">
                  <div className="flex-grow relative min-h-0 w-full">
                    <div className="flex h-full space-x-2">
                      {/* Mermaid editor */}
                      <div className="flex-1 h-full relative">
                        {/* Button container in top right corner */}
                        <div className="absolute top-2 right-2 z-10 flex space-x-1">
                          <button
                            className="px-2 py-1 bg-white/90 hover:bg-white text-gray-600 hover:text-gray-800 border border-gray-300 rounded text-xs shadow-sm transition-colors"
                            onClick={() => {
                              navigator.clipboard.writeText(localContent)
                              // Optional: show a toast notification
                              console.log('Mermaid content copied to clipboard')
                            }}
                            title="Copy diagram code to clipboard"
                            disabled={localIsLocked}
                          >
                            Copy
                          </button>
                        </div>
                        <textarea
                          ref={diagramTextareaRef}
                          className="w-full h-full p-2 border rounded nodrag nowheel resize-none font-mono text-sm pr-16"
                          placeholder="Enter your Mermaid diagram syntax here..."
                          value={localContent}
                          onChange={(e) => {
                            setLocalContent(e.target.value)
                          }}
                          onBlur={() => syncNodeData({ content: localContent })}
                          disabled={localIsLocked}
                          onClick={(e) => e.stopPropagation()}
                          onMouseDown={(e) => {
                            e.stopPropagation()
                          }}
                        />
                      </div>

                      {/* Rendered diagram display */}
                      <div
                        ref={diagramContainerRef}
                        className="flex-1 h-full border rounded p-2 overflow-auto bg-gray-50"
                      >
                        {localDiagramError ? (
                          <div className="text-red-500 p-2">
                            {localDiagramError}
                          </div>
                        ) : (
                          <div
                            dangerouslySetInnerHTML={{
                              __html: DOMPurify.sanitize(localRenderedDiagram),
                            }}
                            className="flex flex-col items-center justify-center h-full gap-10"
                          />
                        )}
                      </div>
                    </div>
                  </div>

                  {!localIsLocked && (
                    <div className="flex space-x-2 mt-2">
                      <button
                        className="flex-1 py-2 px-4 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                        onClick={handleRefreshDiagram}
                      >
                        Refresh Diagram
                      </button>
                      <button
                        className="flex-1 py-2 px-4 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                        onClick={handleGetAISuggestions}
                        disabled={localIsGenerating}
                      >
                        {localIsGenerating
                          ? 'Getting Suggestions...'
                          : 'Get AI Suggestions'}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {localStatusMessage && (
                <div className="text-sm text-gray-600">
                  {localStatusMessage}
                </div>
              )}

              {localAiSuggestions &&
                localAiSuggestions.length > 0 &&
                !localShowDesignMode && (
                  <div className="overflow-auto w-full">
                    <div className="flex justify-between items-center mb-2">
                      <h3 className="font-semibold">AI Suggestions:</h3>
                      <button
                        className="py-1 px-3 bg-red-500 text-white text-xs rounded hover:bg-red-600 transition-colors"
                        onClick={clearAiSuggestions}
                      >
                        Clear Suggestions
                      </button>
                    </div>
                    <div className="space-y-4">
                      {localAiSuggestions.map((suggestion, index) => (
                        <div
                          key={`${id}-suggestion-${index}`}
                          className="border rounded p-3 bg-gray-50"
                        >
                          {/* Suggestion header */}
                          <div className="mb-2">
                            <span className="text-xs font-medium">
                              Suggestion {index + 1}
                            </span>
                          </div>

                          {/* Code and preview side by side */}
                          <div className="flex space-x-2">
                            {/* Code */}
                            <div className="w-1/2">
                              <pre className="whitespace-pre-wrap text-xs font-mono h-56 overflow-auto p-2 bg-white border rounded">
                                {suggestion}
                              </pre>
                            </div>

                            {/* Preview - always shown */}
                            <div className="w-1/2 border rounded p-2 bg-white">
                              <div className="text-xs font-medium mb-1">
                                Preview:
                              </div>
                              <div className="p-2 bg-gray-50 rounded h-48 overflow-auto flex items-center justify-center">
                                {localRenderedSuggestions[index] ? (
                                  <div
                                    dangerouslySetInnerHTML={{
                                      __html: DOMPurify.sanitize(
                                        localRenderedSuggestions[index]
                                      ),
                                    }}
                                    className="w-full h-full flex items-center justify-center scale-[0.9] transform-origin-center"
                                    style={{ transformOrigin: 'center' }}
                                  />
                                ) : (
                                  <div className="text-xs text-gray-500">
                                    Rendering preview...
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="mt-2">
                            <button
                              className="py-1 px-3 bg-blue-500 text-white text-xs rounded hover:bg-blue-600"
                              onClick={() => applySuggestion(suggestion)}
                            >
                              Apply This Suggestion
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
            </div>
          )}
          <AlertDialog
            open={localShowDeleteDialog}
            onOpenChange={setLocalShowDeleteDialog}
          >
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Confirm Deletion</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete this diagram? This action
                  cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleDeleteConfirmed}>
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <Dialog
            open={localShowHelpDialog}
            onOpenChange={(open) => setLocalShowHelpDialog(open)}
          >
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Mermaid Diagram Help</DialogTitle>
              </DialogHeader>
              <div className="flex flex-col space-y-4">
                <p>
                  There are two modes: Code mode, where you can craft diagrams
                  and get AI suggestions for different versions, and Design
                  mode, where you can create new diagrams from scratch using AI
                  assistance.
                </p>
                <p>
                  Mermaid is a JavaScript-based diagramming and charting tool that renders
                  Markdown-inspired text definitions to create and modify diagrams dynamically.
                </p>
                <div className="space-y-3">
                  <h4 className="font-medium">Supported Diagram Types:</h4>
                  <div className="grid grid-cols-1 gap-2 text-sm">
                    {DIAGRAM_TYPES.map((type) => (
                      <div key={type.value} className="flex justify-between">
                        <span className="font-medium">{type.label}:</span>
                        <span className="text-gray-600">
                          {DIAGRAM_HELP[type.value as keyof typeof DIAGRAM_HELP]}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <h4 className="font-medium">Quick Start Example (Flowchart):</h4>
                  <pre className="bg-gray-100 p-3 rounded text-xs overflow-x-auto">
{`graph TD
    A[Start] --> B{Decision}
    B -->|Yes| C[Action 1]
    B -->|No| D[Action 2]
    C --> E[End]
    D --> E`}
                  </pre>
                </div>
                <p>
                  For detailed documentation and examples, visit the Mermaid
                  website or refer to the manual (links provided below).
                </p>
              </div>
              <DialogFooter className="flex justify-end space-x-2">
                <Button variant="outline" asChild>
                  <a
                    href="https://mermaid.js.org/"
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center"
                  >
                    Mermaid Homepage <ExternalLink className="ml-2 h-4 w-4" />
                  </a>
                </Button>
                <Button variant="outline" asChild>
                  <a
                    href="https://mermaid.js.org/syntax/flowchart.html"
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center"
                  >
                    Syntax Guide <ExternalLink className="ml-2 h-4 w-4" />
                  </a>
                </Button>
                <Button onClick={() => setLocalShowHelpDialog(false)}>
                  Close
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {isDataSettingsDialogVisible && (
        <NodeDataSettingsDialog
          visible={isDataSettingsDialogVisible}
          onHide={() => setIsDataSettingsDialogVisible(false)}
          settings={dataSettings}
          nodeId={id}
          onSettingsChange={handleDataSettingsChange}
          onRemoveInputLink={handleRemoveInputLink}
          onSyncInputLink={handleSyncInputLink}
          forceShowAvailableNodes={true}
          debug={false}
        />
      )}
    </NodeBaseContainer>
  )
}

export default memo(NodeDiagramsMermaid) 